import React, { useRef, useEffect } from 'react';
import { Animated, Text, StyleSheet, TouchableOpacity, Easing } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { useThemedStyles } from '../../hooks/useThemedStyles';

export interface KpiTileProps {
  icon: string;
  label: string;
  value: string | number;
  /** Positive/negative change vs. the prior period — renders a colored
   * up/down chip when provided; omitted entirely when there's nothing to
   * compare against yet (no fake placeholder deltas). */
  delta?: number;
  minWidth?: number;
  /** Label lines before truncating (default 1) */
  labelLines?: number;
  /** Staggers this tile's entrance animation relative to its siblings —
   * pass the tile's index in the row. */
  index?: number;
  onPress?: () => void;
}

export const KpiTile: React.FC<KpiTileProps> = ({ icon, label, value, delta, minWidth, labelLines = 1, index = 0, onPress }) => {
  const styles = useThemedStyles(getStyles);
  const anim = useRef(new Animated.Value(0)).current;
  const pressScale = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    Animated.timing(anim, {
      toValue: 1,
      duration: 320,
      delay: index * 60,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();
  }, [anim, index]);

  const translateY = anim.interpolate({ inputRange: [0, 1], outputRange: [12, 0] });

  const handlePressIn = () => Animated.spring(pressScale, { toValue: 0.97, useNativeDriver: true, speed: 40, bounciness: 4 }).start();
  const handlePressOut = () => Animated.spring(pressScale, { toValue: 1, useNativeDriver: true, speed: 40, bounciness: 4 }).start();

  const deltaPositive = (delta ?? 0) >= 0;

  return (
    <Animated.View style={{ flex: 1, minWidth, opacity: anim, transform: [{ translateY }, { scale: pressScale }] }}>
      <TouchableOpacity
        style={styles.tile}
        activeOpacity={onPress ? 0.85 : 1}
        disabled={!onPress}
        onPress={onPress}
        onPressIn={onPress ? handlePressIn : undefined}
        onPressOut={onPress ? handlePressOut : undefined}
      >
        <Ionicons name={icon as any} size={18} color={colors.brand.primary as string} />
        <Text style={styles.value} numberOfLines={1}>{value}</Text>
        <Text style={styles.label} numberOfLines={labelLines}>{label}</Text>
        {delta != null && (
          <Text style={[styles.delta, deltaPositive ? styles.deltaUp : styles.deltaDown]}>
            {deltaPositive ? '▲' : '▼'} {Math.abs(delta)}%
          </Text>
        )}
      </TouchableOpacity>
    </Animated.View>
  );
};

const getStyles = () => StyleSheet.create({
  tile: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    borderWidth: 1,
    borderColor: colors.border,
    paddingVertical: designTokens.spacing.sm,
    paddingHorizontal: designTokens.spacing.md,
    minHeight: 88,
    justifyContent: 'flex-start',
    gap: 2,
    ...designTokens.shadows.sm,
  },
  value: {
    fontSize: 26,
    fontFamily: typography.fontFamily.bold,
    color: colors.text.primary,
  },
  label: {
    fontSize: 11,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  delta: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.semibold,
    marginTop: 2,
  },
  deltaUp: {
    color: colors.status.success,
  },
  deltaDown: {
    color: colors.status.error,
  },
});
